import React, { useState, useEffect, useContext } from "react";
import { ThemeContext } from "../theme";
import { Users, Trophy, Zap, } from 'lucide-react';

function Home() {
  // On load
  const [showContent, setShowContent] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);
  useEffect(() => {
    // Simulate a delay before showing the content
    setTimeout(() => {
      setShowContent(true);
    }, 100); // Adjust the delay time as needed
  }, []);

  // Theme
  const { isDarkMode, toggleDarkMode, colors } = useContext(ThemeContext)!;
  const textColor: string = isDarkMode ? "white" : "black";

  const handleAppStoreClick = () => {
    window.open("https://apps.apple.com/us/app/icallon/id6446251262", "_blank");
  };

  const handleGooglePlayClick = () => {
    window.open("https://play.google.com/store/apps/details?id=com.icallon", "_blank");
  };

  return (
    <div
      className={` ${showContent
        ? "opacity-100 transition-opacity duration-1000"
        : "opacity-0"
        }`}
    >
      <div className={`min-h-screen ${colors.bgSecondaryColor} relative overflow-hidden`}>
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-1000"></div>
          <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        </div>

        <div className="relative z-10">
          {/* Hero Section */}
          <div className="container mx-auto px-6 pt-7 md:pt-16 md:pb-16">
            <div className={`text-center transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
              <div className="mb-8 flex justify-center rounded-full">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-transform duration-300">
                    <img src={process.env.PUBLIC_URL + '/images/icallont.png'} alt="" width={70} height={70} />
                  </div>
                  <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full animate-bounce"></div>
                  <div className="absolute -bottom-2 -left-2 w-4 h-4 bg-blue-400 rounded-full animate-pulse"></div>
                </div>
              </div>

              <h1 className={`text-3xl md:text-5xl font-black ${colors.textColor} mb-6 tracking-tight`}>
                I Call On
              </h1>
              <p className={`text-xl md:text-2xl ${colors.textColor} opacity-80 mb-8 max-w-3xl mx-auto leading-relaxed`}>
                The ultimate word game experience. Challenge friends, test your vocabulary, and dominate the leaderboard in this fast-paced multiplayer adventure.
              </p>

              <div className="flex flex-row gap-4 justify-center">
                <button onClick={handleGooglePlayClick} className="flex flex-col items-center transition-transform hover:scale-105 active:scale-95">
                  <img src={process.env.PUBLIC_URL + '/images/google-play-badge.png'} alt="Google Play" className="h-14" />
                  <p className={`md:text-xl text-lg font-bold text-${textColor}`}>Google Play</p>
                </button>
                <button onClick={handleAppStoreClick} className="flex flex-col items-center transition-transform hover:scale-105 active:scale-95">
                  <img src={process.env.PUBLIC_URL + '/images/app-store-badge.png'} alt="App Store" className="h-14" />
                  <p className={`md:text-xl text-lg font-bold text-${textColor}`}>App Store</p>
                </button>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="container mx-auto px-6 py-16">
            <div className="grid md:grid-cols-3 gap-8">
              <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                  <Users className="h-6 w-6 text-white" />
                </div>
                <h3 className={`text-2xl font-bold ${colors.textColor} mb-4`}>Multiplayer Fun</h3>
                <p className={`${colors.textColor} opacity-70`}>
                  Create games and invite friends with a simple link. Battle it out in real-time with up to 8 players.
                </p>
              </div>

              <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <Zap className="h-6 w-6 text-white" />
                </div>
                <h3 className={`text-2xl font-bold ${colors.textColor} mb-4`}>Fast-Paced Action</h3>
                <p className={`${colors.textColor} opacity-70`}>
                  Quick rounds, instant scoring, and dynamic gameplay keep everyone engaged and on their toes.
                </p>
              </div>

              <div className={`${colors.cardBg} ${colors.borderColor} border rounded-2xl p-8 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl`}>
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-6">
                  <Trophy className="h-6 w-6 text-white" />
                </div>
                <h3 className={`text-2xl font-bold ${colors.textColor} mb-4`}>Competitive Scoring</h3>
                <p className={`${colors.textColor} opacity-70`}>
                  Strategic scoring system rewards creativity and speed. Climb the leaderboard and prove your word mastery.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
